import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { mdiAccountOutline, mdiPhone, mdiShieldCheck } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import CustomMap from "components/CustomMap";
import Header from "components/Header";
import Rater from "components/Rater";
import { ArrowDotRightBigIcon, BookMarkAddIcon } from "components/SVGIcons";
import Textarea from "components/Textarea";
import Tooltip from "components/Tooltip";
import { routes } from "constants/routes";
import { convertDiffToAge, convertTime, parseError } from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { truckInfo, truckLocation } from "helpers/truck";
import MainLayout from "layouts/Main";
import { IQuestion } from "models/Load";
import { ITruckData } from "models/Truck";
import { postBlockUsersAPI } from "services/api/userSetting";
import { AppDispatch } from "store";
import { selectAccount } from "store/auth/selector";
import { handleLoading } from "store/global/loader";
import {
  MUTATION_CREATE_TRUCK_ANSWER,
  MUTATION_CREATE_TRUCK_QUESTION,
  MUTATION_SAVE_TRUCK,
  QUERY_TRUCK_DETAIL,
  QUERY_TRUCK_QUESTION
} from "./query";
import styles from "./style.module.scss";
import FMCSALink from "../../../../components/FMCSALink";

export default function TruckDetail() {
  const { state } = useLocation();
  const { truckId } = useParams();
  const { account } = useSelector(selectAccount);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const [selectedTruckData, setSelectedTruckData] = useState<
    ITruckData & {
      truckAge: string;
      truckAddress: string;
      truckCoords: google.maps.LatLngLiteral[];
    }
  >();
  const isMyTruck = selectedTruckData?.user.id === account.id;

  const { data, loading, error } = useQuery(QUERY_TRUCK_DETAIL, {
    variables: { id: truckId }
  });

  const {
    data: questionData,
    error: questionError,
    refetch
  } = useQuery(QUERY_TRUCK_QUESTION, {
    variables: { truckId: truckId },
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    dispatch(handleLoading(loading));

    if (error) {
      showErrorMessage(error.message);
    }

    if (questionError) {
      showErrorMessage(questionError.message);
    }
  }, [loading, error, questionError]);

  const truckQuestion: IQuestion[] = useMemo(() => {
    if (questionData?.truckQuestions) {
      return questionData.truckQuestions.data ?? [];
    }
    return [];
  }, [questionData]);

  useEffect(() => {
    if (data?.truck) {
      const truck = data.truck;
      const truckAge = convertDiffToAge(dayjs().diff(dayjs(truck.timePosted), "milliseconds"));
      const truckAddress = [
        truck.user?.address,
        truck.user?.city,
        truck.user?.state,
        truck.user?.country
      ]
        .filter((item) => !!item && item !== undefined)
        .join(", ");
      const truckCoords = [];
      if (truck.originCoords)
        truckCoords.push({ lat: truck.originCoords.latitude, lng: truck.originCoords.longitude });
      if (truck.destinationCoords)
        truckCoords.push({
          lat: truck.destinationCoords.latitude,
          lng: truck.destinationCoords.longitude
        });

      setSelectedTruckData({ ...truck, truckAge, truckAddress, truckCoords });
    }
  }, [data]);

  const [CreateQuestion] = useMutation(MUTATION_CREATE_TRUCK_QUESTION, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [CreateAnswer] = useMutation(MUTATION_CREATE_TRUCK_ANSWER, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [SaveTruck] = useMutation(MUTATION_SAVE_TRUCK, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  function goProfile() {
    if (selectedTruckData) navigate(`${routes.USER}/${selectedTruckData.user.id}`);
  }

  const blockTruck = async () => {
    if (!isMyTruck && selectedTruckData) {
      await postBlockUsersAPI(selectedTruckData.user.id)
        .then(() => {
          showSuccessMessage("Successfully blocked user.");
          toggleConfirmModal();
          navigate(routes.TRUCKS);
        })
        .catch(() => {
          showErrorMessage("Failed to block user.");
        });
    } else {
      showErrorMessage("You can't block yourself.");
    }
  };

  const sendTruckQuestion = async () => {
    try {
      dispatch(handleLoading(true));
      await CreateQuestion({
        variables: {
          truckId: truckId,
          question: newQuestion
        }
      });
      await refetch();
    } catch (error) {
      showErrorMessage("Failed to send question.");
    } finally {
      dispatch(handleLoading(false));
    }
    setNewQuestion("");
  };

  const sendTruckAnswer = async (questionId: string) => {
    try {
      dispatch(handleLoading(true));
      await CreateAnswer({
        variables: {
          truckId: truckId,
          questionId: questionId,
          answer: newAnswer
        }
      });
      await refetch();
    } catch (error) {
      showErrorMessage("Failed to answer question.");
    } finally {
      dispatch(handleLoading(false));
    }
    setNewAnswer("");
  };

  const createUserSavedTruck = async () => {
    try {
      await SaveTruck({
        variables: {
          truckId: truckId
        }
      });
      showSuccessMessage("Successfully saved truck.");
    } catch (error) {
      showErrorMessage("Failed to save truck.");
    }
    goTruckList();
  };

  const goToUserProfile = (id: string) => {
    navigate(routes.USER + `/${id}`);
  };

  const goTruckList = () => {
    navigate(routes.TRUCKS_SEARCH);
  };

  return selectedTruckData ? (
    <MainLayout
      desktopTitle="Truck Search"
      className={styles.truckDetailContainer}
      customHeader={<Header label="Truck Details" />}>
      <div className={styles.truckDetailBody}>
        <div className={styles.decorationTopLine}></div>
        <div className={styles.truckDetailHeader}>
          <h3 className={styles.truckDetailHeaderCol}>
            {truckLocation(
              selectedTruckData.originCity,
              selectedTruckData.originRegion,
              selectedTruckData.originRadius,
              "origin"
            )}
            <ArrowDotRightBigIcon className="flex-none w-fit" />{" "}
            {truckLocation(
              selectedTruckData.destinationCity,
              selectedTruckData.destinationRegion,
              selectedTruckData.destinationRadius,
              "destination"
            )}
          </h3>

          <h4 className="text-sm font-semibold">
            {truckInfo(
              selectedTruckData.trailerTypes,
              selectedTruckData.length,
              selectedTruckData.weight
            )}
          </h4>
        </div>

        <ul className={styles.truckDetailCard}>
          <li>
            <span className="text-xs text-placeholder">Ready</span>
            <span>{convertTime(selectedTruckData.timeStart).day}</span>
            <span className="text-xs">{convertTime(selectedTruckData.timeStart).time}</span>
          </li>
          <li>
            <span className="text-xs text-placeholder">Close</span>
            <span>{convertTime(selectedTruckData.timeEnd).day}</span>
            <span className="text-xs">{convertTime(selectedTruckData.timeEnd).time}</span>
          </li>
          <li>
            <span className="text-xs text-placeholder">Truck Age</span>
            <span>{selectedTruckData.truckAge}</span>
          </li>
        </ul>

        <div className={styles.truckDetailBox}>
          <div className={styles.truckDetailBoxCard}>
            <div className={styles.truckDetailCardTitle}>Company</div>

            <div className={styles.truckDetailCardTop}>
              <div className={styles.truckDetailCardTopText}>
                <span className="font-bold capitalize">{selectedTruckData.user?.dba}</span>
              </div>
              <div className={styles.truckDetailCardMedium}>
                <button className="underline" onClick={goProfile}>
                  View Profile
                </button>
                <button className={styles.truckDetailCardTopIcon} onClick={goProfile}>
                  <Icon path={mdiAccountOutline} className="m-auto text-placeholder" />
                </button>
              </div>
              <div className={styles.truckDetailCardMedium}>
                <span>Block:</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  rounded
                  onClick={toggleConfirmModal}>
                  User
                </Button>
              </div>
            </div>
          </div>

          <div className={styles.truckDetailBoxCard}>
            <div className={styles.truckDetailCardTitle}>Company Information</div>

            <div className={styles.truckDetailCardContent}>
              <span>{selectedTruckData.user.mc && `MC#: ${selectedTruckData.user.mc}`}</span>
              <span>{selectedTruckData.user.dot && `DOT#: ${selectedTruckData.user.dot}`}</span>
              <span>{selectedTruckData.details}</span>
              <span>{selectedTruckData.truckAddress}</span>
            </div>
          </div>

          <div className={styles.truckDetailBoxCard}>
            <div className={styles.truckDetailCardTitle}>Contact or Bid</div>

            <div className={styles.truckDetailCardContent}>
              <span>Contact : {selectedTruckData.user.contactName}</span>
              <div className="flex">
                <span>Phone :&nbsp;</span>
                <a
                  href={`tel:+1${selectedTruckData.user.contactPhone}`}
                  className="underline text-info">
                  {selectedTruckData.user.contactPhone}
                </a>
              </div>

              <div className="break-all">
                <span>Email :&nbsp;</span>
                <a href={`mailto:${selectedTruckData.user.email}`} className="underline text-info">
                  {selectedTruckData.user.email}
                </a>
              </div>
              <div className={styles.truckDetailCardContentBottom}>
                <a
                  href={`tel:+1${selectedTruckData.user.contactPhone}`}
                  className={styles.callButton}>
                  <Icon path={mdiPhone} className="m-auto text-white" />
                </a>

                <a
                  href={`mailto:${selectedTruckData.user.email}`}
                  className={styles.truckDetailCardContentButton}>
                  Email
                </a>
              </div>
            </div>
          </div>

          <div className={styles.truckDetailBoxCard}>
            <div className={styles.truckDetailCardTitle}>Reporting</div>

            <div className={styles.truckDetailCardContent}>
              <div className={styles.truckDetailCard4ContentTop}>
                <FMCSALink dot={selectedTruckData.user.dot} mc={selectedTruckData.user.mc}>
                  FMCSA Report
                </FMCSALink>
              </div>
              <div className={styles.truckDetailCard4ContentTop}>
                <Button
                  variant="text"
                  className="!text-info !p-0 h-8"
                  onClick={() => goToUserProfile(selectedTruckData.user.id)}>
                  Rate This User
                </Button>
                <Rater value={selectedTruckData.averageRating / 20} readOnly />
              </div>
              {!selectedTruckData.user?.hasVerifiedEmail ? (
                <Tooltip
                  position="left"
                  message="This carrier has been verified for liability insurance, business legitimacy and authority compliance. We still recommend verifying cargo insurance and other requirements with the carrier as needed.">
                  <div className={styles.truckDetailCardContentBottom}>
                    <span>Haul Pay Verified Carrier</span>
                    <Icon path={mdiShieldCheck} className="text-primary size-11" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.truckDetailRate}>
          <div className={styles.truckDetailRateTitle}>
            <span>Target Rate</span>
          </div>

          <div className={styles.truckDetailRateContent}>
            <span>
              {selectedTruckData.targetRate
                ? `${formatNumber(selectedTruckData.targetRate, 2)} per Mile`
                : "None"}
            </span>
          </div>
        </div>

        {!(state?.isSavedTruck || isMyTruck) ? (
          <Button
            type="button"
            className={styles.truckDetailSave}
            prefixNode={<BookMarkAddIcon />}
            onClick={createUserSavedTruck}>
            Save this Truck
          </Button>
        ) : null}

        <div className={styles.truckDetailAsk}>
          <h4 className="text-xl font-medium">{isMyTruck ? "Answer Question" : "Ask Question"}</h4>

          {truckQuestion.length ? (
            <ul className={styles.questionList}>
              {truckQuestion.map((truckQuestion) => (
                <li key={truckQuestion.id}>
                  <div className={styles.question}>
                    <div className={styles.item}>
                      <div className={styles.headerIcon}>
                        <span className="text-lg">Q</span>
                        <span className="text-xs text-center">
                          {dayjs(truckQuestion.timePosted).format("MM/DD/YY HH:mm")}
                        </span>
                      </div>
                      <p className="text-sm">{truckQuestion.question}</p>
                    </div>
                    {truckQuestion.user !== account?.id && truckQuestion.user ? (
                      <button
                        type="button"
                        className="text-xs text-info"
                        onClick={() => goToUserProfile(truckQuestion.user)}>
                        View Profile
                      </button>
                    ) : null}
                  </div>
                  {truckQuestion.answerSet.length === 0 && isMyTruck ? (
                    <div className={styles.truckDetailSend}>
                      <Textarea
                        value={newAnswer}
                        onChange={(e) => setNewAnswer(e.target.value)}
                        className="flex-1"
                        placeholder="Type your answer here..."
                      />
                      <Button
                        className={styles.truckDetailSendButton}
                        onClick={() => sendTruckAnswer(truckQuestion.id)}>
                        Send
                      </Button>
                    </div>
                  ) : (
                    <>
                      {truckQuestion.answerSet.map((truckAnswer) => (
                        <div key={truckAnswer.id} className={styles.answerWrapper}>
                          <div className={styles.header}>
                            <span className="text-lg">A</span>
                            <span className="text-xs text-center">
                              {dayjs(truckAnswer.timePosted).format("MM/DD/YY HH:mm")}
                            </span>
                          </div>
                          <p className="text-sm">{truckAnswer.answer}</p>
                        </div>
                      ))}
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-placeholder">No questions posted yet.</span>
          )}

          {!isMyTruck ? (
            <div className={styles.truckDetailSend}>
              <Textarea
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                className="flex-1"
                placeholder="Type your question here..."
              />
              <Button className={styles.truckDetailSendButton} onClick={sendTruckQuestion}>
                Send
              </Button>
            </div>
          ) : null}
        </div>

        <div className={styles.truckDetailMap}>
          <h4 className="text-xl font-medium">Map</h4>

          <CustomMap
            points={selectedTruckData.truckCoords}
            center={selectedTruckData.truckCoords[0]}
          />
        </div>
      </div>

      <ConfirmationModal
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        onOK={blockTruck}
        content="Are you sure you want to block all posts by this user? You can manage blocked users from Settings under the Account section."
      />
    </MainLayout>
  ) : null;
}
